"use client";

import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, type MotionProps } from "framer-motion";
import { BodyPortal } from "../../generic/BodyPortal.js";
import { useToastActions, useActiveToasts } from "./ToastContext.js";
import { ToastElem } from "./ToastElem.js";
import type { Toast } from "./Toast.js";
import { useOptionalIsMobile } from "../../accessibility/DeviceInfoContext.js";


const fadeInOut: MotionProps = {
    initial: { opacity: 0, y: 50, scale: 0.7 },
    animate: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: { type: "spring", duration: 0.5, bounce: 0.4 },
    },
    exit: {
        opacity: 0,
        scale: 0.9,
        transition: { duration: 0.25, ease: "easeInOut" },
    },
    transition: {
        layout: { type: "spring", stiffness: 500, damping: 30 },
    },
};

const TOAST_UPDATE_LOOP_MS = 51;
const TOAST_REMOVE_WAIT_MS = 250;
const TOAST_INTERVAL_MS = 300;

export function ToastContainer() {
    const toasts = useActiveToasts();
    const { removeToast, clearAllToasts } = useToastActions();

    const [visibleToasts, setVisibleToasts] = useState<Toast[]>([]);

    const isMobile = useOptionalIsMobile();
    const maxVisibleToasts = (isMobile ? 3 : 5);

    // We access these through a ref to avoid the timer useEffect restarting on every change
    const toastsRef = useRef<Toast[]>(toasts);
    toastsRef.current = toasts;

    const visibleToastsRef = useRef<Toast[]>([]);
    const lastRemoveTimeMsRef = useRef<number>(0);
    const lastAddTimeMsRef = useRef<number>(0);

    // Timer to manage visibility of toasts
    const hasAnyToasts = (toasts.length > 0 || visibleToasts.length > 0);
    useEffect(() => {
        if (!hasAnyToasts)
            return;

        const timerID = setInterval(() => {
            const currentToasts = toastsRef.current;
            const newVisibleToasts = [...visibleToastsRef.current];
            let anyVisibleChanges = false;

            // Remove expired or manually dismissed toasts
            for (let index = 0; index < newVisibleToasts.length; ++index) {
                const toast = newVisibleToasts[index];
                if (!currentToasts.includes(toast) || toast.shouldBeDismissed()) {
                    lastRemoveTimeMsRef.current = Date.now();
                    newVisibleToasts.splice(index, 1);
                    removeToast(toast.id);
                    index -= 1;
                    anyVisibleChanges = true;
                }
            }

            const timeSinceRemoveMs = (Date.now() - lastRemoveTimeMsRef.current);
            const timeSinceAddMs = (Date.now() - lastAddTimeMsRef.current);
            if (timeSinceAddMs >= TOAST_INTERVAL_MS) {
                // Add new toast
                let newToastIsOld = false;
                let newToastToAdd: Toast | null = null;
                for (let index = currentToasts.length - 1; index >= 0; --index) {
                    const toast = currentToasts[index];
                    if (toast.shouldBeDismissed())
                        continue;

                    if (toast.hasBeenShown()) {
                        if (newToastToAdd !== null)
                            break;
                        if (newVisibleToasts.length >= maxVisibleToasts || timeSinceRemoveMs < TOAST_REMOVE_WAIT_MS)
                            break;
                        if (newVisibleToasts.includes(toast))
                            continue;

                        newToastIsOld = true;
                    }
                    newToastToAdd = toast;
                }
                if (newToastToAdd !== null) {
                    if (newToastIsOld) {
                        // Insert old toasts at index 0
                        newVisibleToasts.splice(0, 0, newToastToAdd);
                    } else {
                        newVisibleToasts.push(newToastToAdd);
                    }
                    anyVisibleChanges = true;
                    lastAddTimeMsRef.current = Date.now();
                }

                // Hide toasts beyond maxVisibleToasts
                while (newVisibleToasts.length > maxVisibleToasts) {
                    newVisibleToasts.splice(0, 1);
                    anyVisibleChanges = true;
                    lastRemoveTimeMsRef.current = Date.now();
                }
            }

            // Update the UI
            if (anyVisibleChanges) {
                visibleToastsRef.current = newVisibleToasts;
                setVisibleToasts(newVisibleToasts);
            }
        }, TOAST_UPDATE_LOOP_MS);
        return () => clearInterval(timerID);
    }, [hasAnyToasts, removeToast, maxVisibleToasts]);

    // Escape to dismiss all toasts
    const anyToastsVisible = (visibleToasts.length > 0);
    useEffect(() => {
        if (!anyToastsVisible)
            return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                clearAllToasts();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [anyToastsVisible, clearAllToasts]);

    const toastsInView = [];
    for (let index = Math.max(0, visibleToasts.length - maxVisibleToasts); index < visibleToasts.length; ++index) {
        toastsInView.push(visibleToasts[index]);
    }
    return (
        <BodyPortal>
            <div
                className="pointer-events-none fixed top-6 right-6 left-6 z-1000 *:pointer-events-auto md:top-auto md:right-auto md:bottom-9 md:left-9"
                aria-live="off">
                <AnimatePresence mode="sync">
                    {toastsInView.map((toast, index) => (
                        <motion.div
                            key={toast.id}
                            layout={true}
                            {...fadeInOut}
                            style={{
                                marginBottom: index < visibleToasts.length - 1 ? "calc(var(--spacing)*4)" : 0,
                            }}>
                            <ToastElem
                                toast={toast}
                                onDismiss={removeToast} />
                        </motion.div>
                    ))}
                </AnimatePresence>
            </div>
        </BodyPortal>
    );
}
